import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getOrder, getCustomers, updateOrder, deleteOrder, getPayments, reviewPayment, getPaymentProofSignedUrl, createAdminPayment, downloadBrandedPickupPdf } from '../../services/adminService';
import TtfOrderDetailsCard from './TtfOrderDetailsCard';
import { normalizeInstagram } from '../../utils/validation';
import { artistOrderPaymentMethod } from '../../services/artistOrderService';

const formatPrice = (amount, currency = 'mxn') =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount || 0);

const formatDate = (iso) =>
  new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });

const METHOD_LABELS = {
  bank_transfer: 'Transferencia bancaria',
  mercado_pago: 'Mercado Pago',
  clip: 'Clip',
  stripe: 'Stripe',
  cash: 'Efectivo',
  other: 'Otro',
};

const VALID_STATUSES = [
  { value: 'awaiting_payment', label: 'Esperando pago' },
  { value: 'confirmed',      label: 'Confirmado' },
  { value: 'en_preparacion', label: 'En preparación' },
  { value: 'recoleccion',    label: 'Recolección' },
  { value: 'enviado',        label: 'Enviado' },
  { value: 'entregado',      label: 'Entregado' },
];

const STATUS_CONFIG = {
  awaiting_payment: { label: 'Esperando pago', color: '#8e44ad', bg: '#f4ecf7' },
  confirmed:      { label: 'Confirmado',     color: '#2ca9e1', bg: '#e8f6fd' },
  en_preparacion: { label: 'En preparación', color: '#f0a500', bg: '#fef9e7' },
  recoleccion:    { label: 'Recolección',     color: '#6f42c1', bg: '#f1ecf9' },
  enviado:        { label: 'Enviado',         color: '#e8640c', bg: '#fdf2ec' },
  entregado:      { label: 'Entregado',       color: '#28a745', bg: '#eaf7ed' },
  cancelled:      { label: 'Cancelado',       color: '#dc3545', bg: '#fdecea' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#666', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 12px',
      borderRadius: '12px',
      fontSize: '0.85rem',
      fontWeight: '600',
      color: cfg.color,
      background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
};

// Customer <select> value for "type a name" (no customer account)
const CUSTOM_CUSTOMER = '__custom__';

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const Card = ({ children, style }) => (
  <div style={{
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '1.5rem',
    marginBottom: '1.25rem',
    ...style,
  }}>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 style={{ margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </h3>
);

const AdminOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session, user } = useAuth();
  const proofFileRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fulfillment status form
  const [selectedStatus, setSelectedStatus] = useState('');
  const [tesoroPdfLoading, setTesoroPdfLoading] = useState(false);
  const [tesoroPdfError, setTesoroPdfError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Customer assignment (manual sales only)
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customCustomerName, setCustomCustomerName] = useState('');
  const [assigningSaving, setAssigningSaving] = useState(false);
  const [assignMessage, setAssignMessage] = useState(null);

  // Instagram user — shown on the pickup instructions (recolección email/PDF)
  const [instagramDraft, setInstagramDraft] = useState('');
  const [instagramSaving, setInstagramSaving] = useState(false);
  const [instagramMessage, setInstagramMessage] = useState(null);

  // Partial payments (only relevant when order.allowsPartialPayment)
  const [orderPayments, setOrderPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentActionId, setPaymentActionId] = useState(null);

  // Add-partial-payment form
  const [partialAmount, setPartialAmount] = useState('');
  const [partialMethod, setPartialMethod] = useState('cash');
  const [partialReference, setPartialReference] = useState('');
  const [partialProof, setPartialProof] = useState(null); // { path, filename } | null
  const [partialUploading, setPartialUploading] = useState(false);
  const [partialSaving, setPartialSaving] = useState(false);
  const [partialMessage, setPartialMessage] = useState(null);

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return;
    }
    let cancelled = false;
    setLoading(true);

    getOrder(session.access_token, id)
      .then(data => {
        if (cancelled) return;
        setOrder(data.order);
        setSelectedStatus(data.order.fulfillmentStatus || 'confirmed');
        setInstagramDraft(data.order.customerInstagram || '');
        setCustomCustomerName(data.order.customer?.email ? '' : (data.order.customer?.name || ''));
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.access_token, id]);

  // Load customers only for manual sales without a customer.
  // order.source defaults to 'online' in mapOrderDetail so missing source on old rows is safe.
  useEffect(() => {
    if (!order || order.source !== 'manual' || order.customer?.email) return;
    if (!session?.access_token) return;
    getCustomers(session.access_token, { limit: 100, page: 1 })
      .then(data => setCustomers(data.customers || []))
      .catch(() => {});
  }, [order, session?.access_token]);

  const loadPayments = async () => {
    if (!order?.allowsPartialPayment || !session?.access_token) return;
    setPaymentsLoading(true);
    try {
      const data = await getPayments(session.access_token, { purchase_id: order.id, status: 'all', limit: 50 });
      setOrderPayments(data.payments || []);
    } catch (_) {
      setOrderPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => {
    if (!order?.allowsPartialPayment) return;
    let cancelled = false;
    (async () => {
      setPaymentsLoading(true);
      try {
        const data = await getPayments(session.access_token, { purchase_id: order.id, status: 'all', limit: 50 });
        if (!cancelled) setOrderPayments(data.payments || []);
      } catch (_) {
        if (!cancelled) setOrderPayments([]);
      } finally {
        if (!cancelled) setPaymentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [order?.id, order?.allowsPartialPayment, session?.access_token]);

  const handleViewProof = async (proofPath) => {
    if (!proofPath) return;
    try {
      const data = await getPaymentProofSignedUrl(session.access_token, proofPath);
      if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      alert(`No se pudo abrir el comprobante: ${err.message}`);
    }
  };

  const handleConfirmPayment = async (payment) => {
    if (!window.confirm(`¿Confirmar el pago de ${formatPrice(payment.amount, order.currency)}?`)) return;
    setPaymentActionId(payment.id);
    try {
      await reviewPayment(session.access_token, payment.id, { status: 'confirmed' });
      const refreshed = await getOrder(session.access_token, id);
      setOrder(refreshed.order);
      await loadPayments();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setPaymentActionId(null);
    }
  };

  const handleRejectPayment = async (payment) => {
    const reason = window.prompt('Motivo del rechazo (visible para el cliente):');
    if (!reason || !reason.trim()) return;
    setPaymentActionId(payment.id);
    try {
      await reviewPayment(session.access_token, payment.id, { status: 'rejected', reject_reason: reason.trim() });
      await loadPayments();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setPaymentActionId(null);
    }
  };

  const handleMarkFullyPaid = async () => {
    if (!order?.allowsPartialPayment) return;
    const remaining = Math.max(0, Number(order.totalAmount || 0) - Number(order.amountPaid || 0));
    if (remaining <= 0) return;
    if (!window.confirm(`Registrar un pago manual por ${formatPrice(remaining, order.currency)} y marcar el pedido como pagado en su totalidad?`)) return;
    setPaymentActionId('mark-paid');
    try {
      await createAdminPayment(session.access_token, {
        purchase_id: order.id,
        amount: remaining,
        method: 'cash',
        reference: 'admin manual',
      });
      const refreshed = await getOrder(session.access_token, id);
      setOrder(refreshed.order);
      await loadPayments();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setPaymentActionId(null);
    }
  };

  const handleProofFileChange = async (file) => {
    if (!file) return;
    const MAX_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      setPartialMessage({ type: 'error', text: 'El archivo no puede superar 5 MB.' });
      return;
    }
    if (!user?.id) {
      setPartialMessage({ type: 'error', text: 'Sesión no disponible.' });
      return;
    }
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-80);
    const path = `${user.id}/admin-proof/${order.id}/${Date.now()}_${safeName}`;
    setPartialUploading(true);
    setPartialMessage(null);
    try {
      const { error: uploadErr } = await supabase.storage
        .from('payment_proofs')
        .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
      if (uploadErr) {
        setPartialMessage({ type: 'error', text: `Error al subir el archivo: ${uploadErr.message}` });
        return;
      }
      setPartialProof({ path, filename: file.name });
    } catch (err) {
      setPartialMessage({ type: 'error', text: `Error inesperado: ${err.message}` });
    } finally {
      setPartialUploading(false);
    }
  };

  const handleAddPartialPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(partialAmount);
    if (!amount || amount <= 0) {
      setPartialMessage({ type: 'error', text: 'Ingresa un monto válido mayor a 0.' });
      return;
    }
    const total = Number(order.totalAmount || 0);
    const paid = Number(order.amountPaid || 0);
    const remaining = Math.max(0, total - paid);
    if (amount > remaining + 0.01) {
      setPartialMessage({ type: 'error', text: `El monto no puede superar lo restante (${formatPrice(remaining, order.currency)}).` });
      return;
    }
    setPartialSaving(true);
    setPartialMessage(null);
    try {
      await createAdminPayment(session.access_token, {
        purchase_id: order.id,
        amount,
        method: partialMethod,
        reference: partialReference.trim() || undefined,
        proof_url: partialProof?.path || undefined,
        proof_filename: partialProof?.filename || undefined,
      });
      setPartialAmount('');
      setPartialReference('');
      setPartialProof(null);
      setPartialMessage({ type: 'success', text: 'Pago registrado correctamente.' });
      const refreshed = await getOrder(session.access_token, id);
      setOrder(refreshed.order);
      await loadPayments();
    } catch (err) {
      setPartialMessage({ type: 'error', text: err.message || 'No se pudo registrar el pago.' });
    } finally {
      setPartialSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar el pedido #${order.id}? Esta acción no se puede deshacer.`)) return;
    setDeleting(true);
    try {
      await deleteOrder(session.access_token, order.id);
      navigate('/admin/orders');
    } catch (err) {
      alert(`Error al eliminar el pedido: ${err.message}`);
      setDeleting(false);
    }
  };

  const handleDownloadTesoroPdf = async () => {
    setTesoroPdfLoading(true);
    setTesoroPdfError(null);
    try {
      const { blob, filename } = await downloadBrandedPickupPdf(session.access_token, id, 'tesoro');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setTesoroPdfError(err.message || 'No se pudo generar el PDF.');
    } finally {
      setTesoroPdfLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedStatus || selectedStatus === order.fulfillmentStatus) return;
    // Orders don't record the delivery method, so confirm before emailing
    // pickup instructions to someone who may be expecting a shipment.
    if (selectedStatus === 'recoleccion' && !window.confirm(
      'Se enviará al cliente un correo con las instrucciones para recoger en tienda. ¿Es un pedido con recolección en tienda?'
    )) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const data = await updateOrder(session.access_token, id, { fulfillmentStatus: selectedStatus });
      setOrder(prev => ({ ...prev, fulfillmentStatus: data.fulfillmentStatus }));
      setSaveMessage({ type: 'success', text: 'Estado actualizado.' });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message || 'No se pudo actualizar.' });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignCustomer = async () => {
    if (!selectedCustomerId) return;
    const isCustom = selectedCustomerId === CUSTOM_CUSTOMER;
    if (isCustom && !customCustomerName.trim()) {
      setAssignMessage({ type: 'error', text: 'Escribe el nombre del cliente.' });
      return;
    }
    setAssigningSaving(true);
    setAssignMessage(null);
    try {
      const data = await updateOrder(session.access_token, id,
        isCustom ? { customerName: customCustomerName.trim() } : { customerId: selectedCustomerId });
      // The assigned customer may bring a saved Instagram user
      setOrder(prev => ({ ...prev, customer: data.customer, customerInstagram: data.customerInstagram }));
      setInstagramDraft(data.customerInstagram || '');
      setAssignMessage({ type: 'success', text: 'Cliente asignado correctamente.' });
    } catch (err) {
      setAssignMessage({ type: 'error', text: err.message || 'No se pudo asignar el cliente.' });
    } finally {
      setAssigningSaving(false);
    }
  };

  const handleSaveInstagram = async () => {
    const handle = normalizeInstagram(instagramDraft);
    if (handle === undefined) {
      setInstagramMessage({ type: 'error', text: 'Usuario de Instagram no válido.' });
      return;
    }
    setInstagramSaving(true);
    setInstagramMessage(null);
    try {
      const data = await updateOrder(session.access_token, id, { customerInstagram: handle });
      setOrder(prev => ({ ...prev, customerInstagram: data.customerInstagram }));
      setInstagramDraft(data.customerInstagram || '');
      setInstagramMessage({ type: 'success', text: 'Instagram guardado.' });
    } catch (err) {
      setInstagramMessage({ type: 'error', text: err.message || 'No se pudo guardar.' });
    } finally {
      setInstagramSaving(false);
    }
  };

  if (loading) return <div className="admin-loading">Cargando pedido...</div>;

  if (error) {
    return (
      <div className="container" style={{ padding: '4rem 0' }}>
        <Link to="/admin/orders" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Pedidos</Link>
        <p style={{ color: '#dc3545', marginTop: '1rem' }}>{error}</p>
      </div>
    );
  }

  const { customer, items = [] } = order;
  // Compare normalized, so '@Foo' vs saved 'foo' doesn't count as a change
  const instagramUnchanged = (normalizeInstagram(instagramDraft) ?? instagramDraft) === (order.customerInstagram || '');
  const isManual = order.source === 'manual';
  const hasCustomer = !!customer?.email;

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <Link to="/admin/orders" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>
        ← Pedidos
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', margin: '0.75rem 0 1.75rem' }}>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.5rem', color: '#333', margin: 0 }}>
          Pedido #{order.id}
        </h1>
        <StatusBadge status={order.fulfillmentStatus} />
        {isManual && (
          <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600', background: '#eaf7ed', color: '#28a745' }}>
            Venta directa
          </span>
        )}
        {order.source === 'ttf' && (
          <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600', background: '#fdecef', color: '#E4405F' }}>
            Taipei Toy Festival
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: '#888', fontSize: '0.85rem' }}>
          {formatDate(order.createdAt)}
        </span>
      </div>

      <div className="admin-order-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: '1.25rem', alignItems: 'start' }}>
        {/* Left column */}
        <div>
          {/* Customer */}
          <Card>
            <SectionTitle>Cliente</SectionTitle>
            {hasCustomer ? (
              <>
                <p style={{ margin: '0 0 0.4rem', fontWeight: '600', color: '#333' }}>{customer.name || '—'}</p>
                <p style={{ margin: '0 0 0.2rem', color: '#555', fontSize: '0.9rem' }}>{customer.email}</p>
                {customer.address && (
                  <p style={{ margin: '0.75rem 0 0', color: '#555', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    {customer.address}<br />
                    {[customer.city, customer.state, customer.zipCode].filter(Boolean).join(', ')}
                    {customer.country && <><br />{customer.country}</>}
                  </p>
                )}
              </>
            ) : customer?.name ? (
              // Free-typed name on a manual sale (no customer account)
              <>
                <p style={{ margin: '0 0 0.4rem', fontWeight: '600', color: '#333' }}>{customer.name}</p>
                <p style={{ margin: 0, color: '#aaa', fontSize: '0.85rem', fontStyle: 'italic' }}>Sin cuenta de cliente</p>
              </>
            ) : (
              <p style={{ margin: 0, color: '#aaa', fontSize: '0.9rem', fontStyle: 'italic' }}>
                Sin cliente asignado
              </p>
            )}

            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #f0f0f0' }}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.35rem' }}>
                Instagram
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={instagramDraft}
                  onChange={e => { setInstagramDraft(e.target.value); setInstagramMessage(null); }}
                  placeholder="@usuario"
                  autoCapitalize="none"
                  spellCheck={false}
                  style={inputStyle}
                />
                <button
                  onClick={handleSaveInstagram}
                  disabled={instagramSaving || instagramUnchanged}
                  style={{
                    padding: '0.6rem 1rem', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600,
                    background: '#2ca9e1', color: 'white', cursor: 'pointer', whiteSpace: 'nowrap',
                    opacity: instagramSaving || instagramUnchanged ? 0.5 : 1,
                  }}
                >
                  {instagramSaving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', color: instagramMessage ? (instagramMessage.type === 'success' ? '#28a745' : '#dc3545') : '#999' }}>
                {instagramMessage ? instagramMessage.text : 'Se muestra en las instrucciones de recolección y, si el pedido tiene cliente vinculado, se guarda en su perfil.'}
              </p>
            </div>
          </Card>

          {/* Customer assignment panel — manual sales only, no customer yet */}
          {isManual && !hasCustomer && (
            <Card>
              <SectionTitle>Asignar cliente</SectionTitle>
              <select
                value={selectedCustomerId}
                onChange={e => { setSelectedCustomerId(e.target.value); setAssignMessage(null); }}
                style={{ ...inputStyle, cursor: 'pointer', marginBottom: '0.75rem', color: selectedCustomerId ? '#333' : '#999' }}
              >
                <option value="">— Seleccionar cliente —</option>
                <option value={CUSTOM_CUSTOMER}>✏️ Otro (escribir nombre)</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name ? `${c.name} (${c.email})` : c.email}
                  </option>
                ))}
              </select>
              {selectedCustomerId === CUSTOM_CUSTOMER && (
                <input
                  type="text"
                  autoFocus
                  maxLength={120}
                  value={customCustomerName}
                  onChange={e => { setCustomCustomerName(e.target.value); setAssignMessage(null); }}
                  placeholder="Nombre del cliente"
                  aria-label="Nombre del cliente"
                  style={{ ...inputStyle, marginBottom: '0.75rem' }}
                />
              )}

              {assignMessage && (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: assignMessage.type === 'success' ? '#28a745' : '#dc3545' }}>
                  {assignMessage.text}
                </p>
              )}

              <button
                onClick={handleAssignCustomer}
                disabled={assigningSaving || !selectedCustomerId}
                style={{
                  width: '100%',
                  padding: '0.65rem 1rem',
                  background: !selectedCustomerId ? '#f0f0f0' : assigningSaving ? '#ccc' : '#2ca9e1',
                  color: !selectedCustomerId ? '#aaa' : 'white',
                  border: 'none', borderRadius: '8px',
                  fontSize: '0.9rem', fontWeight: '600',
                  cursor: assigningSaving || !selectedCustomerId ? 'default' : 'pointer',
                }}
              >
                {assigningSaving ? 'Asignando...' : 'Asignar cliente'}
              </button>
            </Card>
          )}

          {/* Items */}
          <Card>
            <SectionTitle>Artículos</SectionTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ textAlign: 'left', padding: '0 0 0.5rem', color: '#888', fontWeight: '600' }}>Producto</th>
                  <th style={{ textAlign: 'center', padding: '0 0 0.5rem', color: '#888', fontWeight: '600' }}>Cant.</th>
                  <th style={{ textAlign: 'right', padding: '0 0 0.5rem', color: '#888', fontWeight: '600' }}>Precio</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.id ?? idx} style={{ borderBottom: '1px solid #f8f8f8' }}>
                    <td style={{ padding: '0.6rem 0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {(item.variantImage || item.image) && (
                        <img
                          src={item.variantImage || item.image}
                          alt={item.name}
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', flexShrink: 0 }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span style={{ color: '#333' }}>
                        {item.isPreorder ? `Anticipo — ${item.name}` : item.name}
                        {item.variantName && (
                          <span style={{ color: '#888', fontWeight: '400' }}> · {item.variantName}</span>
                        )}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center', color: '#555' }}>{item.quantity || 1}</td>
                    <td style={{ padding: '0.6rem 0', textAlign: 'right', color: '#333', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      {formatPrice((item.price || 0) * (item.quantity || 1), order.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
              <span style={{ fontWeight: '700', fontSize: '1.05rem', color: '#E4405F' }}>
                Total: {formatPrice(order.totalAmount, order.currency)}
              </span>
            </div>
          </Card>

          {/* Payment info */}
          <Card>
            <SectionTitle>Pago</SectionTitle>
            {isManual ? (
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#888', fontStyle: 'italic' }}>
                Venta registrada manualmente — sin transacción de Stripe
              </p>
            ) : artistOrderPaymentMethod(order.paymentIntentId) ? (
              <div style={{ fontSize: '0.9rem', color: '#555' }}>
                <p style={{ margin: 0 }}>
                  <strong>Pago directo al artista</strong> —{' '}
                  {artistOrderPaymentMethod(order.paymentIntentId) === 'payment_link'
                    ? 'el cliente paga con el link de cada pieza'
                    : 'el cliente transfiere a la CLABE del artista (concepto: su Instagram)'}
                  . Registra el pago abajo cuando el artista confirme que lo recibió.
                </p>
                {(order.items || []).filter((item) => item.payment_link_url).map((item) => (
                  <p key={`${item.id}-${item.variantId || ''}`} style={{ margin: '0.4rem 0 0' }}>
                    {item.name}:{' '}
                    <a href={item.payment_link_url} target="_blank" rel="noopener noreferrer" style={{ color: '#2ca9e1' }}>
                      link de {item.payment_link_provider === 'clip' ? 'Clip' : 'Mercado Pago'}
                    </a>
                  </p>
                ))}
              </div>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#555' }}>
                  <span style={{ color: '#888' }}>ID Stripe: </span>
                  <code style={{ fontSize: '0.85rem', color: '#333' }}>{order.paymentIntentId}</code>
                </p>
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem', color: '#555' }}>
                  <span style={{ color: '#888' }}>Estado Stripe: </span>
                  <span style={{ color: '#28a745', fontWeight: '500' }}>{order.status}</span>
                </p>
              </>
            )}
          </Card>

          {order.source === 'ttf' && (
            <TtfOrderDetailsCard
              token={session?.access_token}
              order={order}
              onOrderChanged={async () => {
                const refreshed = await getOrder(session.access_token, id);
                setOrder(refreshed.order);
              }}
            />
          )}

          {/* Partial payments — only for orders that allow it */}
          {order.allowsPartialPayment && (() => {
            const total = Number(order.totalAmount || 0);
            const paid = Number(order.amountPaid || 0);
            const remaining = Math.max(0, total - paid);
            const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
            const fullyPaid = order.paymentStatus === 'paid' || remaining <= 0.01;
            return (
              <Card>
                <SectionTitle>Pagos parciales</SectionTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                  <strong>{formatPrice(paid, order.currency)} de {formatPrice(total, order.currency)}</strong>
                  <span style={{ fontSize: '0.85rem', color: fullyPaid ? '#28a745' : '#666' }}>
                    {fullyPaid ? 'Pagado en su totalidad' : `Faltan ${formatPrice(remaining, order.currency)}`}
                  </span>
                </div>
                <div style={{ width: '100%', height: '8px', background: '#eee', borderRadius: '999px', overflow: 'hidden', marginBottom: '0.75rem' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: fullyPaid ? '#28a745' : '#2ca9e1' }} />
                </div>
                {!fullyPaid && (
                  <button
                    type="button"
                    onClick={handleMarkFullyPaid}
                    disabled={paymentActionId !== null}
                    style={{
                      padding: '0.5rem 0.9rem',
                      background: paymentActionId !== null ? '#9bd0ec' : '#2ca9e1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: paymentActionId !== null ? 'wait' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.85rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    {paymentActionId === 'mark-paid' ? 'Registrando...' : 'Marcar como pagado en su totalidad'}
                  </button>
                )}
                {/* Add partial payment form */}
                {!fullyPaid && (
                  <form onSubmit={handleAddPartialPayment} style={{ marginBottom: '1rem', padding: '0.85rem', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #eee' }}>
                    <p style={{ margin: '0 0 0.6rem', fontSize: '0.82rem', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Registrar pago parcial
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div style={{ flex: '1 1 100px', minWidth: '100px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#888', marginBottom: '0.3rem' }}>Monto</label>
                        <input
                          type="number"
                          min="1"
                          max={Math.max(0, remaining)}
                          step="0.01"
                          placeholder={`Máx. ${remaining.toFixed(2)}`}
                          value={partialAmount}
                          onChange={e => { setPartialAmount(e.target.value); setPartialMessage(null); }}
                          required
                          style={{ ...inputStyle, width: '100%' }}
                        />
                      </div>
                      <div style={{ flex: '1 1 110px', minWidth: '110px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#888', marginBottom: '0.3rem' }}>Método</label>
                        <select
                          value={partialMethod}
                          onChange={e => setPartialMethod(e.target.value)}
                          style={{ ...inputStyle, cursor: 'pointer' }}
                        >
                          <option value="cash">Efectivo</option>
                          <option value="bank_transfer">Transferencia bancaria</option>
                          <option value="mercado_pago">Mercado Pago</option>
                          <option value="clip">Clip</option>
                          <option value="other">Otro</option>
                        </select>
                      </div>
                      <div style={{ flex: '2 1 140px', minWidth: '140px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#888', marginBottom: '0.3rem' }}>Referencia (opcional)</label>
                        <input
                          type="text"
                          maxLength={200}
                          placeholder="Folio, nota..."
                          value={partialReference}
                          onChange={e => setPartialReference(e.target.value)}
                          style={{ ...inputStyle, width: '100%' }}
                        />
                      </div>
                      <div style={{ flex: '1 1 120px', minWidth: '120px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#888', marginBottom: '0.3rem' }}>Comprobante (opcional)</label>
                        <input
                          ref={proofFileRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                          style={{ display: 'none' }}
                          onChange={e => {
                            const f = e.target.files?.[0];
                            if (f) handleProofFileChange(f);
                            e.target.value = '';
                          }}
                        />
                        {partialProof ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <span style={{ fontSize: '0.78rem', color: '#28a745', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '110px' }}>
                              {partialProof.filename}
                            </span>
                            <button
                              type="button"
                              onClick={() => setPartialProof(null)}
                              style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.9rem', padding: 0, lineHeight: 1, flexShrink: 0 }}
                              title="Quitar archivo"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => proofFileRef.current?.click()}
                            disabled={partialUploading}
                            style={{
                              padding: '0.4rem 0.7rem',
                              background: partialUploading ? '#ddd' : 'white',
                              color: partialUploading ? '#aaa' : '#555',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              cursor: partialUploading ? 'wait' : 'pointer',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            {partialUploading ? 'Subiendo...' : 'Adjuntar archivo'}
                          </button>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={partialSaving || partialUploading}
                        style={{
                          padding: '0.6rem 1rem',
                          background: partialSaving || partialUploading ? '#9bd0ec' : '#2ca9e1',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          cursor: partialSaving || partialUploading ? 'wait' : 'pointer',
                          whiteSpace: 'nowrap',
                          alignSelf: 'flex-end',
                        }}
                      >
                        {partialSaving ? 'Registrando...' : 'Registrar pago'}
                      </button>
                    </div>
                    {partialMessage && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: partialMessage.type === 'success' ? '#28a745' : '#dc3545' }}>
                        {partialMessage.text}
                      </p>
                    )}
                  </form>
                )}

                {order.mercadoPagoUrl && (
                  <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: '#666' }}>
                    Link MP del cliente:{' '}
                    <a href={order.mercadoPagoUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#2ca9e1' }}>
                      Abrir
                    </a>
                  </p>
                )}
                {paymentsLoading && <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>Cargando pagos...</p>}
                {!paymentsLoading && orderPayments.length === 0 && (
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#888' }}>Aún no hay pagos registrados.</p>
                )}
                {!paymentsLoading && orderPayments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {orderPayments.map((p) => (
                      <div key={p.id} style={{ padding: '0.6rem 0.75rem', background: '#fafafa', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <strong>{formatPrice(p.amount, order.currency)}</strong>
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.82rem', color: '#666' }}>
                            {METHOD_LABELS[p.method] || p.method} · {formatDate(p.createdAt)}
                          </span>
                          {p.reference && (
                            <div style={{ fontSize: '0.78rem', color: '#888' }}>Ref: {p.reference}</div>
                          )}
                          {p.rejectReason && (
                            <div style={{ fontSize: '0.78rem', color: '#dc3545' }}>Motivo: {p.rejectReason}</div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '999px',
                            fontSize: '0.72rem',
                            fontWeight: '600',
                            background: p.status === 'confirmed' ? '#eaf7ed' : p.status === 'rejected' ? '#fdecee' : '#fff4e0',
                            color: p.status === 'confirmed' ? '#28a745' : p.status === 'rejected' ? '#dc3545' : '#e8640c',
                          }}>
                            {p.status === 'confirmed' ? 'Confirmado' : p.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                          </span>
                          {p.proofUrl && (
                            <button
                              type="button"
                              onClick={() => handleViewProof(p.proofUrl)}
                              style={{ background: 'none', border: 'none', color: '#2ca9e1', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600' }}
                            >
                              Comprobante
                            </button>
                          )}
                          {p.status === 'pending_review' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleConfirmPayment(p)}
                                disabled={paymentActionId === p.id}
                                style={{ background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}
                              >
                                Confirmar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRejectPayment(p)}
                                disabled={paymentActionId === p.id}
                                style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}
                              >
                                Rechazar
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })()}
        </div>

        {/* Right column — fulfillment form */}
        <div>
          <Card>
            <SectionTitle>Actualizar estado</SectionTitle>
            <select
              value={selectedStatus}
              onChange={e => { setSelectedStatus(e.target.value); setSaveMessage(null); }}
              style={{
                width: '100%',
                padding: '0.6rem 0.75rem',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '0.9rem',
                marginBottom: '0.75rem',
                cursor: 'pointer',
              }}
            >
              {VALID_STATUSES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>

            {saveMessage && (
              <p style={{
                margin: '0 0 0.75rem',
                fontSize: '0.85rem',
                color: saveMessage.type === 'success' ? '#28a745' : '#dc3545',
              }}>
                {saveMessage.text}
              </p>
            )}

            <button
              onClick={handleSave}
              disabled={saving || selectedStatus === order.fulfillmentStatus}
              style={{
                width: '100%',
                padding: '0.65rem 1rem',
                background: selectedStatus === order.fulfillmentStatus ? '#f0f0f0' : '#2ca9e1',
                color: selectedStatus === order.fulfillmentStatus ? '#aaa' : 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: saving || selectedStatus === order.fulfillmentStatus ? 'default' : 'pointer',
              }}
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </Card>

          {/* El Tesoro del Juguete pickup PDF for direct sales — downloaded and handed
              over in person, never emailed. Lists every product in the sale. */}
          {order.source === 'manual' && (
            <Card>
              <SectionTitle>Recolección El Tesoro del Juguete</SectionTitle>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>
                PDF con las instrucciones de recolección y los productos de esta venta directa. No se envía por correo.
              </p>
              {tesoroPdfError && (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#dc3545' }}>{tesoroPdfError}</p>
              )}
              <button
                onClick={handleDownloadTesoroPdf}
                disabled={tesoroPdfLoading}
                style={{
                  width: '100%',
                  padding: '0.65rem 1rem',
                  background: tesoroPdfLoading ? '#c9b3ec' : '#7016d0',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: tesoroPdfLoading ? 'default' : 'pointer',
                }}
              >
                {tesoroPdfLoading ? 'Generando PDF...' : 'Descargar PDF'}
              </button>
            </Card>
          )}

          <Card>
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                width: '100%',
                padding: '0.65rem 1rem',
                background: deleting ? '#f5c6cb' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: deleting ? 'wait' : 'pointer',
              }}
            >
              {deleting ? 'Eliminando...' : 'Eliminar pedido'}
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetail;
